import { getIcon } from "@components/icon";
import YearSelector from "@components/inputs/date/YearSelector";
import { IInputOnChangeEvent } from "@components/inputs/input";
import { ElectronicSubmissionTypeCode, VatStatementFrequencyCode } from "@odata/GeneratedEnums";
import { getSortedFYs, isFiscalYearActive, isFiscalYearClosed } from "@pages/fiscalYear/FiscalYear.utils";
import memoizeOne from "@utils/memoizeOne";
import { capitalize } from "@utils/string";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { WithPermissionContext, withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../enums";
import TestIds from "../../testIds";
import DateType, { DateFormat, getUtcDateBy, getUtcDayjs } from "../../types/Date";
import {
    ElCol,
    ElectronicSubmissionCellStyled,
    GridStyled,
    HeaderCol,
    HeaderTile,
    IconWrapper,
    RowHeaderCell,
    TileTitle
} from "./ElectronicSubmission.styles";
import {
    ElectronicSubmissionNamespaces,
    ISubmissionOverviewRow,
    SubmissionStatus,
    SubmissionTypeColumnOrder
} from "./ElectronicSubmission.utils";
import ElectronicSubmissionTableCell from "./ElectronicSubmissionTableCell";
import { isOssDeadlineMissed } from "./VatSubmission.utils";

interface IProps extends WithPermissionContext, WithTranslation {
    rows: ISubmissionOverviewRow[];
    year: number;
    onSelectYear?: (year: number) => void;
    onSelectDetail?: (type: ElectronicSubmissionTypeCode, month: number) => void;
    columns: ElectronicSubmissionTypeCode[];
}

class ElectronicSubmissionOverviewGrid extends React.PureComponent<IProps> {
    static contextType = AppContext;

    handleChangeYear = (e: IInputOnChangeEvent<Date>) => {
        const year = getUtcDayjs(e.value).get("year");
        this.props.onSelectYear?.(year);
    };

    handleSelect = (type: ElectronicSubmissionTypeCode, date: Date) => {
        this.props.onSelectDetail(type, date.getMonth());
    };

    getSortedColumns = memoizeOne(() => {
        return this.props.columns.sort((a, b) => SubmissionTypeColumnOrder[a] - SubmissionTypeColumnOrder[b]);
    }, () => [this.props.columns]);

    getColumnIndexes = memoizeOne((): Partial<Record<ElectronicSubmissionTypeCode, number>> => {
        const sortedColumns = this.getSortedColumns();
        const indexes: Partial<Record<ElectronicSubmissionTypeCode, number>> = {};

        sortedColumns.forEach((column, index) => {
            indexes[column] = index + 2;
        });

        return indexes;
    }, () => [this.props.columns]);


    renderVatStatementCell(date: Date, index: number, type: ElectronicSubmissionTypeCode) {
        const row = this.props.rows[index];
        // value for the grid that starts from 1 + 1 for the header (icons)
        const rowIndex = index + 2;
        const submissionInfo = row?.[type];
        const key = `${index}:${type}`;

        if (!submissionInfo || submissionInfo.status === SubmissionStatus.NonExisting) {
            return null;
        }

        const rowSpan = submissionInfo.frequency === VatStatementFrequencyCode.Monthly ? 1 : 3;
        const colIndexes = this.getColumnIndexes();

        if (submissionInfo.status === SubmissionStatus.Empty) {
            return (
                <ElectronicSubmissionCellStyled key={key}
                                                $rowSpan={rowSpan}
                                                $rowIndex={rowIndex}
                                                $colIndex={colIndexes[type]}
                                                data-testid={TestIds.ElSubPeriodCell}
                                                data-id={`${rowIndex - 1}:${colIndexes[type] - 1}`}
                />
            );
        }

        // we are displaying end of period date
        const displayedDate = getUtcDayjs(date).endOf("month").add(rowSpan - 1, "months").toDate();
        let warningMessage = "";
        let warningStatus: Status;

        if (submissionInfo.isSplittable) {
            warningMessage = this.props.t("ElectronicSubmission:VatSubmission.SplittableQuarterlyWarningDescription");
            warningStatus = Status.Error;
        } else if (submissionInfo.isCorrective) {
            // deadline is 25th of the next month
            // https://solitea-cz.atlassian.net/browse/DEV-30547
            const deadline = getUtcDayjs(displayedDate).startOf("month").add(1, "month").set("date", 25);
            const today = getUtcDayjs();
            const isDeadlineMissed = today.isAfter(deadline, "date");

            warningMessage = this.props.t(`ElectronicSubmission:VatSubmission.${isDeadlineMissed ? "DeadlineMissedTableCellWarning" : "CorrectionStillAvailable"}`);
            warningStatus = Status.Warning;
        } else if (type === ElectronicSubmissionTypeCode.VatOSSStatement && submissionInfo.status === SubmissionStatus.Unlocked) {
            if (isOssDeadlineMissed(date)) {
                warningMessage = this.props.t("ElectronicSubmission:VatSubmission.DeadlineMissedWarning");
                warningStatus = Status.Warning;
            }
        }

        return (
            <ElectronicSubmissionTableCell key={key}
                                           date={date}
                                           displayedDate={displayedDate}
                                           rowIndex={rowIndex}
                                           colIndex={colIndexes[type]}
                                           rowSpan={rowSpan}
                                           type={type}
                                           onSelect={this.handleSelect}
                                           isLocked={submissionInfo.status === SubmissionStatus.Locked}
                                           isSelected={submissionInfo.isSelected}
                                           warningMessage={warningMessage}
                                           warningStatus={warningStatus}/>
        );
    }

    renderRow = (date: Date, index: number) => {
        return (
            <React.Fragment key={date.getTime().toString()}>
                <RowHeaderCell>
                    {capitalize(DateType.format(date, DateFormat.month))}
                </RowHeaderCell>
                {this.getSortedColumns().map(type => {
                    return this.renderVatStatementCell(date, index, type);
                })}
            </React.Fragment>
        );
    };

    renderRows = (year: number) => {
        const rows = [];

        for (let i = 0; i < 12; i++) {
            const date = getUtcDateBy(year, i, 1);

            rows.push(this.renderRow(date, i));
        }

        return rows;
    };

    renderHeaderTile = (type: ElectronicSubmissionTypeCode) => {
        let iconName: string;
        let title: string;

        switch (type) {
            case ElectronicSubmissionTypeCode.VatStatement:
                iconName = "VatStatement";
                title = this.props.t("ElectronicSubmission:VatSubmission.VatSt");
                break;
            case ElectronicSubmissionTypeCode.VatControlStatement:
                iconName = "VatControlStatement";
                title = this.props.t("ElectronicSubmission:VatSubmission.VatControlSt");
                break;
            case ElectronicSubmissionTypeCode.VatVIESStatement:
                iconName = "VatViesStatement";
                title = this.props.t("ElectronicSubmission:VatSubmission.VatViesSt");
                break;
            case ElectronicSubmissionTypeCode.VatOSSStatement:
                iconName = "VatOssStatement";
                title = this.props.t("ElectronicSubmission:VatSubmission.VatOssSt");
                break;
            default:
                iconName = "VatStatement";
                title = "missing";
                break;
        }


        const Icon = getIcon(iconName);

        return (
            <HeaderTile>
                <IconWrapper>
                    <Icon/>
                </IconWrapper>
                <TileTitle data-testid={TestIds.Title}>
                    {title}
                </TileTitle>
            </HeaderTile>
        );
    };

    render() {
        if (!this.props.rows) {
            return null;
        }

        const context = this.context as IAppContext;
        const fiscalYears = getSortedFYs(context).filter(fy => isFiscalYearClosed(fy) || isFiscalYearActive(fy));
        const minDate = fiscalYears[0].DateStart;
        const maxDate = fiscalYears[fiscalYears.length - 1].DateEnd;
        const sortedColumns = this.getSortedColumns();

        return (
            <GridStyled $colsNum={sortedColumns.length}>
                <ElCol>
                    <YearSelector value={getUtcDayjs().set("year", this.props.year).toDate()}
                                  onChange={this.handleChangeYear}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                  isMain
                                  isCompact
                    />
                </ElCol>
                {sortedColumns.map(col => {
                    return (
                        <HeaderCol key={col}>
                            {this.renderHeaderTile(col)}
                        </HeaderCol>
                    );
                })}

                {this.renderRows(this.props.year)}
            </GridStyled>
        );
    }
}

export default withTranslation(ElectronicSubmissionNamespaces)(withPermissionContext(ElectronicSubmissionOverviewGrid));